export interface IssueData {
    issueType: string;
    client: string;
    userEmail: string;
    conversationId: string;
    model: string;
    timestamp: string;
    lastUserPrompt?: string;
    modelError?: string;
    conversationSummary?: string;
    browser: string;
    url: string;
}

export function formatSupportEmail(issueData: IssueData): string {
    const parts: string[] = [];

    // Header fields
    if (issueData.issueType) parts.push(`Issue Type: ${issueData.issueType}`);
    if (issueData.client) parts.push(`Client: ${issueData.client}`);
    if (issueData.userEmail) parts.push(`User Email: ${issueData.userEmail}`);
    if (issueData.conversationId) parts.push(`Conversation ID: ${issueData.conversationId}`);
    if (issueData.model) parts.push(`Model Used: ${issueData.model}`);
    if (issueData.timestamp) parts.push(`Timestamp: ${issueData.timestamp}`);

    let result = parts.join('\n');

    // Blank line separation
    result += '\n\n';

    if (issueData.lastUserPrompt) {
        result += `Last User Prompt:\n${issueData.lastUserPrompt}\n\n`;
    }

    if (issueData.modelError) {
        result += `Model Error Message:\n${issueData.modelError}\n\n`;
    }

    if (issueData.conversationSummary) {
        result += `Recent Conversation Summary:\n${issueData.conversationSummary}\n\n`;
    }

    result += `Client Info:\n`;
    if (issueData.browser) result += `Browser: ${issueData.browser}\n`;
    if (issueData.timestamp) result += `Timestamp: ${issueData.timestamp}\n`;
    if (issueData.url) result += `URL: ${issueData.url}\n`;

    return result.trim();
}

export interface DetectedError {
    type: string;
    message: string;
    tool?: string;
}

export function detectError(messages: any[]): DetectedError | null {
    if (!messages || messages.length === 0) return null;

    // We only care about the most recent assistant message
    let lastMessage: any = null;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant' || messages[i].type === 'error') {
            lastMessage = messages[i];
            break;
        }
    }

    if (!lastMessage) return null;

    // Layer 1: Structured Errors
    if (lastMessage.type === 'error' || lastMessage.content?.content_type === 'error') {
        return {
            type: 'System Error',
            message: lastMessage.content?.parts || lastMessage.message || lastMessage.data || 'An unknown system error occurred.'
        };
    }

    const content = typeof lastMessage.content === 'object' ? (lastMessage.content?.parts || '') : (lastMessage.content || '');

    // Layer 2: Tool Response Errors
    // Find if the message contains a tool_response markdown block
    const toolResponseRegex = /```tool_response\s*\n([\s\S]*?)```/g;
    let toolErrorDetected: DetectedError | null = null;
    let match;
    
    while ((match = toolResponseRegex.exec(content)) !== null) {
        try {
            const json = JSON.parse(match[1]);
            if (json.success === false) {
                // Return the first detected tool error
                toolErrorDetected = {
                    type: 'Tool Execution Error',
                    message: json.error || json.message || 'The tool failed to execute properly.',
                    tool: json.tool_name || 'System Tool'
                };
                break;
            }
        } catch (e) {
            // ignore parsing errors
        }
    }
    
    if (toolErrorDetected) return toolErrorDetected;

    // Layer 3: Text Fallback
    const lowerContent = content.toLowerCase();
    
    if (
        lowerContent.includes("error") || 
        lowerContent.includes("failed") || 
        lowerContent.includes("unable") || 
        lowerContent.includes("access denied") ||
        lowerContent.includes("trouble accessing")
    ) {
        // Extract 2-3 sentences max for the fallback message
        const sentences = content.split(/(?<=[.!?])\s+/);
        const preview = sentences.slice(0, 3).join(' ').trim();
        
        // Let's refine the type based on keywords
        let errorType = 'General Error';
        if (lowerContent.includes("access denied")) errorType = 'Access Denied Error';
        else if (lowerContent.includes("memory tool")) errorType = 'Memory Tool Error';
        
        return {
            type: errorType,
            message: preview || "An unexpected error occurred during the response."
        };
    }

    return null;
}
